"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluggableBaseChart = void 0;
var tslib_1 = require("tslib");
var sdk_model_1 = require("@gooddata/sdk-model");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var sdk_ui_charts_1 = require("@gooddata/sdk-ui-charts");
var react_1 = tslib_1.__importDefault(require("react"));
var react_dom_1 = require("react-dom");
var bucket_1 = require("../../../constants/bucket");
var properties_1 = require("../../../constants/properties");
var supportedProperties_1 = require("../../../constants/supportedProperties");
var uiConfig_1 = require("../../../constants/uiConfig");
var Visualization_1 = require("../../../interfaces/Visualization");
var bucketConfig_1 = require("../../../utils/bucketConfig");
var bucketHelper_1 = require("../../../utils/bucketHelper");
var colors_1 = require("../../../utils/colors");
var dimensions_1 = require("../../../utils/dimensions");
var domHelper_1 = require("../../../utils/domHelper");
var propertiesHelper_1 = require("../../../utils/propertiesHelper");
var sort_1 = require("../../../utils/sort");
var translations_1 = require("../../../utils/translations");
var baseChartUiConfigHelper_1 = require("../../../utils/uiConfigHelpers/baseChartUiConfigHelper");
var visualizationsHelper_1 = require("../../../utils/visualizationsHelper");
var BaseChartConfigurationPanel_1 = tslib_1.__importDefault(require("../../configurationPanels/BaseChartConfigurationPanel"));
var AbstractPluggableVisualization_1 = require("../AbstractPluggableVisualization");
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var set_1 = tslib_1.__importDefault(require("lodash/set"));
var tail_1 = tslib_1.__importDefault(require("lodash/tail"));
var drillDownUtil_1 = require("../drillDownUtil");
var PluggableBaseChart = /** @class */ (function (_super) {
    tslib_1.__extends(PluggableBaseChart, _super);
    function PluggableBaseChart(props) {
        var _this = _super.call(this, props) || this;
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        _this.handlePushData = function (data) {
            var resultingData = data;
            if (data.colors) {
                _this.handleConfirmedColorMapping(data);
            }
            else {
                _this.pushData(tslib_1.__assign(tslib_1.__assign({}, resultingData), { references: _this.references }));
            }
        };
        _this.projectId = props.projectId;
        _this.environment = props.environment;
        _this.type = sdk_ui_1.VisualizationTypes.COLUMN;
        _this.featureFlags = props.featureFlags ? props.featureFlags : {};
        _this.ignoreUndoRedo = false;
        _this.defaultControlsProperties = {};
        _this.setCustomControlsProperties({});
        _this.renderFun = props.renderFun;
        _this.supportedPropertiesList = _this.getSupportedPropertiesList();
        return _this;
    }
    PluggableBaseChart.prototype.unmount = function () {
        domHelper_1.unmountComponentsAtNodes([this.element, this.configPanelElement]);
    };
    PluggableBaseChart.prototype.getUiConfig = function () {
        return cloneDeep_1.default(uiConfig_1.DEFAULT_BASE_CHART_UICONFIG);
    };
    PluggableBaseChart.prototype.getExtendedReferencePoint = function (referencePoint) {
        var clonedReferencePoint = cloneDeep_1.default(referencePoint);
        var uiConfig = this.getUiConfig();
        var newReferencePoint = tslib_1.__assign(tslib_1.__assign({}, clonedReferencePoint), { uiConfig: uiConfig });
        this.configureBuckets(newReferencePoint);
        newReferencePoint = bucketConfig_1.configurePercent(newReferencePoint, false);
        newReferencePoint = bucketConfig_1.configureOverTimeComparison(newReferencePoint, !!this.featureFlags["enableWeekFilters"]);
        newReferencePoint = baseChartUiConfigHelper_1.setBaseChartUiConfigRecommendations(newReferencePoint, this.type, !!this.featureFlags["enableWeekFilters"]);
        newReferencePoint = propertiesHelper_1.getReferencePointWithSupportedProperties(newReferencePoint, this.supportedPropertiesList);
        newReferencePoint = baseChartUiConfigHelper_1.setBaseChartUiConfig(newReferencePoint, this.intl, this.type);
        newReferencePoint = sort_1.removeSort(newReferencePoint);
        return Promise.resolve(bucketHelper_1.sanitizeFilters(newReferencePoint));
    };
    PluggableBaseChart.prototype.getInsightWithDrillDownApplied = function (source, drillDownContext) {
        var intersection = drillDownContext.event.drillContext.intersection;
        var withFilters = drillDownUtil_1.addIntersectionFiltersToInsight(source, intersection);
        return drillDownUtil_1.modifyBucketsAttributesForDrillDown(withFilters, drillDownContext.drillDefinition);
    };
    PluggableBaseChart.prototype.isOpenAsReportSupported = function () {
        return visualizationsHelper_1.isOpenAsReportSupportedByVisualization(this.type);
    };
    PluggableBaseChart.prototype.setCustomControlsProperties = function (customControlsProperties) {
        this.customControlsProperties = customControlsProperties;
    };
    PluggableBaseChart.prototype.getExecution = function (options, insight, executionFactory) {
        var _a, _b;
        var dateFormat = options.dateFormat, executionConfig = options.executionConfig;
        var supportedControls = this.getSupportedControls(insight, options);
        return (_a = (_b = executionFactory
            .forInsight(insight))
            .withDimensions.apply(_b, this.getDimensions(insight)))
            .withSorting.apply(_a, sort_1.createSorts(this.type, insight, canSortStackTotalValue(insight, supportedControls))).withDateFormat(dateFormat)
            .withExecConfig(executionConfig);
    };
    PluggableBaseChart.prototype.configureBuckets = function (extendedReferencePoint) {
        var _a, _b, _c, _d, _e;
        var buckets = (_a = extendedReferencePoint === null || extendedReferencePoint === void 0 ? void 0 : extendedReferencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var categoriesCount = (_e = (_d = (_c = (_b = extendedReferencePoint === null || extendedReferencePoint === void 0 ? void 0 : extendedReferencePoint.uiConfig) === null || _b === void 0 ? void 0 : _b.buckets) === null || _c === void 0 ? void 0 : _c[sdk_ui_1.BucketNames.VIEW]) === null || _d === void 0 ? void 0 : _d.itemsLimit) !== null && _e !== void 0 ? _e : uiConfig_1.MAX_CATEGORIES_COUNT;
        set_1.default(extendedReferencePoint, bucket_1.BUCKETS, [
            {
                localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                items: bucketHelper_1.getFilteredMeasuresForStackedCharts(buckets),
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.VIEW,
                items: bucketHelper_1.getAttributeItemsWithoutStacks(buckets).slice(0, categoriesCount),
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.STACK,
                items: this.getStackItems(buckets),
            },
        ]);
    };
    PluggableBaseChart.prototype.getSupportedPropertiesList = function () {
        return supportedProperties_1.BASE_CHART_SUPPORTED_PROPERTIES;
    };
    PluggableBaseChart.prototype.getStackItems = function (buckets) {
        var measures = bucketHelper_1.getMeasureItems(buckets);
        var masterMeasures = bucketHelper_1.filterOutDerivedMeasures(measures);
        var allAttributes = bucketHelper_1.getAllAttributeItemsWithPreference(buckets, [
            sdk_ui_1.BucketNames.LOCATION,
            sdk_ui_1.BucketNames.VIEW,
            sdk_ui_1.BucketNames.TREND,
            sdk_ui_1.BucketNames.STACK,
            sdk_ui_1.BucketNames.SEGMENT,
        ]);
        var stacks = bucketHelper_1.getStackItems(buckets);
        if (masterMeasures.length <= 1 && allAttributes.length > 1) {
            // first attribute is taken, find next available non-date attribute
            var attributesWithoutFirst = tail_1.default(allAttributes);
            var nonDate = attributesWithoutFirst.filter(bucketHelper_1.isNotDateBucketItem);
            stacks = nonDate.slice(0, 1);
        }
        return stacks;
    };
    PluggableBaseChart.prototype.checkBeforeRender = function (insight) {
        _super.prototype.checkBeforeRender.call(this, insight);
        if (!sdk_model_1.insightHasMeasures(insight)) {
            throw new Visualization_1.InvalidBucketsSdkError();
        }
        return true;
    };
    PluggableBaseChart.prototype.renderVisualization = function (options, insight, executionFactory) {
        var _a = options.dimensions, dimensions = _a === void 0 ? { height: undefined } : _a, _b = options.custom, custom = _b === void 0 ? {} : _b, locale = options.locale, theme = options.theme;
        var height = dimensions.height;
        // keep height undef for AD; causes indigo-visualizations to pick default 100%
        var resultingHeight = this.environment === properties_1.DASHBOARDS_ENVIRONMENT ? height : undefined;
        var drillableItems = custom.drillableItems;
        var supportedControls = this.getSupportedControls(insight, options);
        var configSupportedControls = isEmpty_1.default(supportedControls) ? null : supportedControls;
        var fullConfig = this.buildVisualizationConfig(options, configSupportedControls);
        var execution = this.getExecution(options, insight, executionFactory);
        this.renderFun(react_1.default.createElement(sdk_ui_charts_1.BaseChart, { execution: execution, afterRender: this.afterRender, drillableItems: drillableItems, onDrill: this.onDrill, onError: this.onError, onExportReady: this.onExportReady, onLoadingChanged: this.onLoadingChanged, pushData: this.handlePushData, height: resultingHeight, type: this.type, locale: locale, config: sdk_ui_charts_1.updateConfigWithSettings(fullConfig, this.featureFlags), LoadingComponent: null, ErrorComponent: null, theme: theme }), document.querySelector(this.element));
    };
    PluggableBaseChart.prototype.initializeProperties = function (visualizationProperties) {
        var controls = visualizationProperties === null || visualizationProperties === void 0 ? void 0 : visualizationProperties.controls;
        var supportedProperties = propertiesHelper_1.getSupportedPropertiesControls(controls, this.supportedPropertiesList);
        var initialProperties = {
            supportedProperties: { controls: supportedProperties },
        };
        this.pushData({
            initialProperties: initialProperties,
        });
    };
    PluggableBaseChart.prototype.renderConfigurationPanel = function (insight) {
        if (document.querySelector(this.configPanelElement)) {
            react_dom_1.render(react_1.default.createElement(BaseChartConfigurationPanel_1.default, { locale: this.locale, references: this.references, properties: this.visualizationProperties, propertiesMeta: this.propertiesMeta, insight: insight, colors: this.colors, pushData: this.handlePushData, type: this.type, isError: this.getIsError(), isLoading: this.isLoading, featureFlags: this.featureFlags, axis: this.axis }), document.querySelector(this.configPanelElement));
        }
    };
    PluggableBaseChart.prototype.getDimensions = function (insight) {
        return dimensions_1.generateDimensions(insight, this.type);
    };
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    PluggableBaseChart.prototype.handleConfirmedColorMapping = function (data) {
        var resultingData = data;
        this.colors = data.colors;
        if (propertiesHelper_1.isEmptyObject(this.references)) {
            resultingData.references = {};
        }
        else if (this.references) {
            resultingData.references = this.references;
        }
        if (this.visualizationProperties) {
            resultingData.properties = colors_1.getValidProperties(this.visualizationProperties, data.colors.colorAssignments);
            this.visualizationProperties = resultingData.properties;
        }
        this.renderConfigurationPanel(this.currentInsight);
        var openAsReportConfig = this.getOpenAsReportConfig(resultingData.properties);
        if (this.ignoreUndoRedo) {
            this.ignoreUndoRedo = false;
            this.pushData(resultingData);
        }
        else {
            this.pushData(tslib_1.__assign({ openAsReport: openAsReportConfig, ignoreUndoRedo: true }, resultingData));
        }
    };
    PluggableBaseChart.prototype.buildVisualizationConfig = function (options, supportedControls) {
        var _a = options.config, config = _a === void 0 ? {} : _a, _b = options.customVisualizationConfig, customVisualizationConfig = _b === void 0 ? {} : _b;
        var colorMapping = supportedControls === null || supportedControls === void 0 ? void 0 : supportedControls.colorMapping;
        var validColorMapping = colorMapping &&
            colorMapping
                .filter(function (mapping) { return mapping != null; })
                .map(function (mapItem) { return ({
                predicate: sdk_ui_charts_1.ColorUtils.getColorMappingPredicate(mapItem.id),
                color: mapItem.color,
            }); });
        return tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({ separators: config.separators, colorPalette: config.colorPalette, forceDisableDrillOnAxes: config.forceDisableDrillOnAxes }, supportedControls), { colorMapping: validColorMapping && validColorMapping.length > 0 ? validColorMapping : null }), customVisualizationConfig);
    };
    PluggableBaseChart.prototype.getOpenAsReportConfig = function (properties) {
        var hasMapping = propertiesHelper_1.hasColorMapping(properties);
        var isSupported = this.isOpenAsReportSupported();
        var warningMessage = hasMapping ? translations_1.getTranslation("export_unsupported.colors", this.intl) : "";
        return {
            supported: isSupported && !hasMapping,
            warningMessage: warningMessage,
        };
    };
    PluggableBaseChart.prototype.getSupportedControls = function (insight, options) {
        var _a, _b;
        var supportedControls = cloneDeep_1.default((_b = (_a = this.visualizationProperties) === null || _a === void 0 ? void 0 : _a.controls) !== null && _b !== void 0 ? _b : {});
        var defaultControls = propertiesHelper_1.getSupportedPropertiesControls(this.defaultControlsProperties, this.supportedPropertiesList);
        var customControls = propertiesHelper_1.getSupportedPropertiesControls(this.customControlsProperties, this.supportedPropertiesList);
        var legendPosition = this.getLegendPosition(supportedControls, insight, options);
        // Set legend position by bucket items and environment
        set_1.default(supportedControls, "legend.position", legendPosition);
        if (this.environment === properties_1.DASHBOARDS_ENVIRONMENT) {
            var legendResponsiveness = this.featureFlags["enableKDWidgetCustomHeight"]
                ? "autoPositionWithPopup"
                : true;
            set_1.default(supportedControls, "legend.responsive", legendResponsiveness);
        }
        supportedControls = propertiesHelper_1.getHighchartsAxisNameConfiguration(supportedControls, this.featureFlags["enableAxisNameConfiguration"]);
        supportedControls = propertiesHelper_1.getDataPointsConfiguration(supportedControls, this.featureFlags["enableHidingOfDataPoints"]);
        return tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, defaultControls), supportedControls), customControls);
    };
    PluggableBaseChart.prototype.getLegendPosition = function (controlProperties, insight, options) {
        var _a, _b, _c;
        var legendPosition = (_b = (_a = controlProperties === null || controlProperties === void 0 ? void 0 : controlProperties.legend) === null || _a === void 0 ? void 0 : _a.position) !== null && _b !== void 0 ? _b : "auto";
        if (this.environment === properties_1.DASHBOARDS_ENVIRONMENT) {
            var width = (_c = options.dimensions) === null || _c === void 0 ? void 0 : _c.width;
            return width !== undefined && width <= getMaxWidthForCollapsedLegend(legendPosition)
                ? "top"
                : legendPosition;
        }
        return legendPosition === "auto" && isStacked(insight) ? "right" : legendPosition;
    };
    PluggableBaseChart.prototype.isMultipleDatesEnabled = function () {
        //this is development FF and will be removed in the end of dev cycle
        return !!this.featureFlags["enableMultipleDates"];
    };
    return PluggableBaseChart;
}(AbstractPluggableVisualization_1.AbstractPluggableVisualization));
exports.PluggableBaseChart = PluggableBaseChart;
function isStacked(insight) {
    return !sdk_model_1.bucketsIsEmpty(sdk_model_1.insightBuckets(insight, sdk_ui_1.BucketNames.STACK, sdk_ui_1.BucketNames.SEGMENT));
}
function areAllMeasuresOnSingleAxis(insight, secondaryYAxis) {
    var _a, _b;
    var measureCount = sdk_model_1.insightMeasures(insight).length;
    var numberOfMeasureOnSecondaryAxis = (_b = (_a = secondaryYAxis.measures) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    return numberOfMeasureOnSecondaryAxis === 0 || measureCount === numberOfMeasureOnSecondaryAxis;
}
function canSortStackTotalValue(insight, supportedControls) {
    var _a, _b;
    var stackMeasures = (_a = supportedControls === null || supportedControls === void 0 ? void 0 : supportedControls.stackMeasures) !== null && _a !== void 0 ? _a : false;
    var secondaryAxis = (_b = supportedControls === null || supportedControls === void 0 ? void 0 : supportedControls.secondary_yaxis) !== null && _b !== void 0 ? _b : { measures: [] };
    var allMeasuresOnSingleAxis = areAllMeasuresOnSingleAxis(insight, secondaryAxis);
    return stackMeasures && allMeasuresOnSingleAxis;
}
var MAX_WIDTH_FOR_COLLAPSED_LEGEND = 440;
var MAX_WIDTH_FOR_COLLAPSED_AUTO_LEGEND = 610;
function getMaxWidthForCollapsedLegend(legendPosition) {
    return legendPosition === "auto" ? MAX_WIDTH_FOR_COLLAPSED_AUTO_LEGEND : MAX_WIDTH_FOR_COLLAPSED_LEGEND;
}
//# sourceMappingURL=PluggableBaseChart.js.map